/** MongoDB ObjectId hex string */
const OBJECT_ID_HEX = /^[a-fA-F0-9]{24}$/;

/**
 * Split a scanned / pasted ticket string (e.g. multiple bets joined with |).
 */
export function parseTicketScanSegments(raw: string): string[] {
  return raw
    .split(/[|\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Pick one bet id for GET /api/bets/:id — first valid ObjectId segment, else first segment.
 */
export function getPrimaryBetIdForLookup(raw: string): string {
  const segments = parseTicketScanSegments(raw);
  const oid = segments.find((s) => OBJECT_ID_HEX.test(s));
  if (oid) return oid;
  return segments[0] ?? raw.trim();
}

/**
 * All Mongo ObjectId segments on the ticket (for multi-leg UI hints).
 */
export function listObjectIdsOnTicket(raw: string): string[] {
  return parseTicketScanSegments(raw).filter((s) => OBJECT_ID_HEX.test(s));
}

const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

function code128PngDataUrl(data: string): string | null {
  if (typeof document === 'undefined') return null;

  const text = data.trim().replace(/[^\x20-\x7e]/g, '').slice(0, 48);
  if (!text) return null;

  const codes = [104, ...Array.from(text, (char) => char.charCodeAt(0) - 32)];
  const checksum = codes.reduce((sum, code, index) => sum + (index === 0 ? code : code * index), 0) % 103;
  codes.push(checksum, 106);

  const modules: boolean[] = [];
  for (const code of codes) {
    const pattern = CODE128_PATTERNS[code];
    if (!pattern) return null;

    Array.from(pattern).forEach((widthChar, index) => {
      const isBar = index % 2 === 0;
      const width = Number(widthChar);
      for (let i = 0; i < width; i += 1) modules.push(isBar);
    });
  }

  const quietModules = 10;
  const moduleWidth = modules.length > 180 ? 1 : 2;
  const height = 58;
  const width = (modules.length + quietModules * 2) * moduleWidth;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#000';
  modules.forEach((isBar, index) => {
    if (isBar) ctx.fillRect((quietModules + index) * moduleWidth, 0, moduleWidth, height);
  });

  return canvas.toDataURL('image/png');
}

/**
 * Fetch wide short Code128 barcode as PNG data URL for thermal printer.
 * Kept function name for compatibility with existing imports.
 */
export async function qrPngDataUrlForTicket(data: string): Promise<string | null> {
  const trimmed = data.trim();
  if (!trimmed) return null;
  const localBarcode = code128PngDataUrl(trimmed);
  if (localBarcode) return localBarcode;

  try {
    const url =
      `https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(trimmed)}` +
      '&scale=2&height=7&includetext=false';
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read QR image'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
